import * as React from 'react';
import ReactSelect, { ValueType, ActionMeta } from 'react-select';
import Creatable from 'react-select/creatable';
import { MenuPlacement } from 'react-select/src/types';
import { FixedSizeList } from 'react-window';
import classnames from 'classnames';
import { OptionProps } from 'react-select/src/components/Option';
import { MenuListComponentProps } from 'react-select/src/components/Menu';

import { SelectOption } from './types';

// The default theme overrides the CSS classes
import { customStyles } from './customStyles';
import styles from './Select.module.scss';

const OPTION_HEIGHT = 40;
const ROWS = 6;

const MenuList = ({ options, children, getValue }: MenuListComponentProps<SelectOption, false>) => {
    const [value] = getValue();
    const initialOffset =
        options.indexOf(value) !== -1
            ? Array.isArray(children) && children.length >= ROWS
                ? options.indexOf(value) >= ROWS
                    ? options.indexOf(value) * OPTION_HEIGHT - OPTION_HEIGHT * 5
                    : 0
                : 0
            : 0;

    return Array.isArray(children) ? (
        <FixedSizeList
            height={children.length >= ROWS ? OPTION_HEIGHT * ROWS : children.length * OPTION_HEIGHT}
            width="100%"
            itemCount={children.length}
            itemSize={OPTION_HEIGHT}
            initialScrollOffset={initialOffset}
        >
            {({ style, index }) => {
                return <div style={style}>{children[index]}</div>;
            }}
        </FixedSizeList>
    ) : (
        <div>{children}</div>
    );
};

const Option = ({ children, isSelected, innerProps }: OptionProps<SelectOption, false>) => {
    return (
        <div
            className={classnames('react-select__option', styles.option, {
                'react-select__option_selected': isSelected,
            })}
            style={{ height: OPTION_HEIGHT }}
            id={innerProps.id}
            tabIndex={innerProps.tabIndex}
            onClick={innerProps.onClick}
        >
            {children}
        </div>
    );
};

interface SelectProps {
    name: string;
    options: Array<SelectOption>;
    value: ValueType<SelectOption, false>;
    placeholder?: string;
    onChange: (value: ValueType<SelectOption, false>, actionMeta: ActionMeta<SelectOption>) => void;
    isDisabled?: boolean;
    isClearable?: boolean;
    isCreatable?: boolean;
    ariaLabel?: string;
    fixedWidth?: number;
    menuPlacement?: MenuPlacement;
    useReactWindow?: boolean;
}

export const Select: React.FC<SelectProps> = ({
    name,
    options,
    value,
    placeholder = 'Select...',
    onChange,
    isDisabled = false,
    isClearable = false,
    isCreatable = false,
    ariaLabel = 'select component',
    menuPlacement = 'bottom',
    fixedWidth,
    useReactWindow,
    ...otherProps
}) => {
    if (isCreatable) {
        return (
            <Creatable
                options={options}
                aria-label={ariaLabel}
                onChange={onChange}
                placeholder={placeholder}
                value={value}
                styles={customStyles(fixedWidth)}
                isDisabled={isDisabled}
                menuPlacement={menuPlacement}
                {...otherProps}
            />
        );
    }

    return (
        <ReactSelect
            options={options}
            isDisabled={isDisabled}
            isClearable={isClearable}
            closeMenuOnSelect
            aria-label={ariaLabel}
            onChange={onChange}
            placeholder={placeholder}
            value={value}
            styles={customStyles(fixedWidth)}
            disabled={isDisabled}
            menuPlacement={menuPlacement}
            components={
                useReactWindow
                    ? {
                          MenuList,
                          Option,
                      }
                    : undefined
            }
            {...otherProps}
        />
    );
};
